import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { ordersAPI, productsAPI, subscriptionsAPI, customersAPI } from '../lib/api';
import { BarChart3, TrendingUp, Package, ShoppingCart, Users, DollarSign, Activity } from 'lucide-react';

interface Stats {
    totalOrders: number;
    totalRevenue: number;
    totalProducts: number;
    activeSubscriptions: number;
    totalCustomers?: number;
    averageOrderValue?: number;
}

interface OrderStats {
    totalOrders: number;
    totalRevenue: number;
    pendingOrders: number;
    confirmedOrders: number;
    deliveredOrders: number;
    cancelledOrders: number;
    averageOrderValue: number;
}

const Reports = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState<Stats>({
        totalOrders: 0,
        totalRevenue: 0,
        totalProducts: 0,
        activeSubscriptions: 0,
    });
    const [orderStats, setOrderStats] = useState<OrderStats>({
        totalOrders: 0,
        totalRevenue: 0,
        pendingOrders: 0,
        confirmedOrders: 0,
        deliveredOrders: 0,
        cancelledOrders: 0,
        averageOrderValue: 0,
    });
    const [loading, setLoading] = useState(true);
    const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');
    const [user, setUser] = useState<any>(null);

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            setUser(JSON.parse(userData));
        }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }
        fetchStats();
    }, [timeRange]);

    const fetchStats = async () => {
        try {
            const isDelivery = JSON.parse(localStorage.getItem('user') || '{}').role === 'delivery';

            const [ordersRes, productsRes, subsRes, customersRes] = await Promise.all([
                ordersAPI.getStats().catch(() => ({ data: {} as OrderStats })),
                !isDelivery ? productsAPI.getAll().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
                !isDelivery ? subscriptionsAPI.getStats().catch(() => ({ data: { active: 0 } })) : Promise.resolve({ data: { active: 0 } }),
                !isDelivery ? customersAPI.getStats().catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
            ]);

            const orderData = ordersRes.data || {};
            const products = productsRes.data || [];
            const subsData = subsRes.data || {};
            const customersData = customersRes.data || {};

            setStats({
                totalOrders: orderData.totalOrders || 0,
                totalRevenue: orderData.totalRevenue || 0,
                totalProducts: products.length || 0,
                activeSubscriptions: subsData.active || 0,
                totalCustomers: customersData.totalCustomers || 0,
                averageOrderValue: orderData.averageOrderValue || 0,
            });

            setOrderStats({
                totalOrders: orderData.totalOrders || 0,
                totalRevenue: orderData.totalRevenue || 0,
                pendingOrders: orderData.pendingOrders || 0,
                confirmedOrders: orderData.confirmedOrders || 0,
                deliveredOrders: orderData.deliveredOrders || 0,
                cancelledOrders: orderData.cancelledOrders || 0,
                averageOrderValue: orderData.averageOrderValue || 0,
            });
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                </div>
            </Layout>
        );
    }

    const isDelivery = user?.role === 'delivery';

    const reportCards = [
        {
            title: isDelivery ? 'Earnings This Period' : 'Total Revenue',
            value: `₹${stats.totalRevenue.toLocaleString()}`,
            icon: DollarSign,
            color: 'bg-gradient-to-br from-green-500 to-green-600',
            change: '+12%'
        },
        {
            title: isDelivery ? 'Deliveries Done' : 'Total Orders',
            value: stats.totalOrders.toLocaleString(),
            icon: ShoppingCart,
            color: 'bg-gradient-to-br from-blue-500 to-blue-600',
            change: '+8%'
        },
        ...(!isDelivery ? [
            {
                title: 'Products',
                value: stats.totalProducts.toLocaleString(),
                icon: Package,
                color: 'bg-gradient-to-br from-purple-500 to-purple-600',
                change: '+5%'
            },
            {
                title: 'Active Subscriptions',
                value: stats.activeSubscriptions.toLocaleString(),
                icon: Users,
                color: 'bg-gradient-to-br from-orange-500 to-orange-600',
                change: '+15%'
            },
        ] : []),
    ];

    const orderStatusData = [
        { label: 'Pending', value: orderStats.pendingOrders, color: 'bg-yellow-500' },
        { label: 'Confirmed', value: orderStats.confirmedOrders, color: 'bg-blue-500' },
        { label: 'Delivered', value: orderStats.deliveredOrders, color: 'bg-green-500' },
        { label: 'Cancelled', value: orderStats.cancelledOrders, color: 'bg-red-500' },
    ];

    const maxStatusValue = Math.max(...orderStatusData.map(d => d.value), 1);

    return (
        <Layout>
            <div className="mb-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Reports & Analytics</h1>
                        <p className="text-gray-500">Business insights and performance metrics</p>
                    </div>
                    <div className="flex gap-2">
                        {(['7d', '30d', '90d', 'all'] as const).map((range) => (
                            <button
                                key={range}
                                onClick={() => setTimeRange(range)}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors ${timeRange === range
                                    ? 'bg-green-600 text-white'
                                    : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                                    }`}
                            >
                                {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : range === '90d' ? '90 Days' : 'All Time'}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {reportCards.map((card) => (
                    <div key={card.title} className={`${card.color} rounded-2xl p-6 text-white shadow-lg`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                <card.icon size={24} />
                            </div>
                            <span className="text-sm bg-white/20 px-2 py-1 rounded-lg">{card.change}</span>
                        </div>
                        <p className="text-3xl font-bold mb-1">{card.value}</p>
                        <p className="text-sm text-white/80">{card.title}</p>
                    </div>
                ))}
            </div>

            {/* Additional Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Activity className="text-blue-600" size={20} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Average Order Value</p>
                            <p className="text-2xl font-bold text-gray-800">₹{orderStats.averageOrderValue.toLocaleString()}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <TrendingUp className="text-green-600" size={20} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Delivery Rate</p>
                            <p className="text-2xl font-bold text-gray-800">
                                {orderStats.totalOrders > 0
                                    ? Math.round((orderStats.deliveredOrders / orderStats.totalOrders) * 100)
                                    : 0}%
                            </p>
                        </div>
                    </div>
                </div>

                {!isDelivery && (
                    <div className="bg-white rounded-xl p-6 shadow-sm">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <Users className="text-purple-600" size={20} />
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Total Customers</p>
                                <p className="text-2xl font-bold text-gray-800">{stats.totalCustomers?.toLocaleString() || '0'}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Order Status Distribution */}
            <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
                <div className="flex items-center gap-3 mb-6">
                    <BarChart3 className="text-green-600" size={24} />
                    <h2 className="text-lg font-semibold text-gray-800">{isDelivery ? 'Delivery Performance' : 'Order Status Distribution'}</h2>
                </div>
                <div className="space-y-4">
                    {orderStatusData.map((item) => (
                        <div key={item.label}>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium text-gray-700">{item.label}</span>
                                <span className="text-sm font-bold text-gray-800">{item.value}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                <div
                                    className={`${item.color} h-full rounded-full transition-all duration-500`}
                                    style={{ width: `${(item.value / maxStatusValue) * 100}%` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Revenue & Performance Summary */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                        <DollarSign className="text-green-600" size={24} />
                        <h2 className="text-lg font-semibold text-gray-800">Revenue Summary</h2>
                    </div>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                            <span className="text-gray-700">Total Revenue</span>
                            <span className="text-xl font-bold text-green-600">₹{stats.totalRevenue.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
                            <span className="text-gray-700">Average Order Value</span>
                            <span className="text-xl font-bold text-blue-600">₹{orderStats.averageOrderValue.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg">
                            <span className="text-gray-700">Total Orders</span>
                            <span className="text-xl font-bold text-purple-600">{stats.totalOrders.toLocaleString()}</span>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                        <Activity className="text-blue-600" size={24} />
                        <h2 className="text-lg font-semibold text-gray-800">Performance Metrics</h2>
                    </div>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                            <span className="text-gray-700">Delivery Success Rate</span>
                            <span className="text-xl font-bold text-green-600">
                                {orderStats.totalOrders > 0
                                    ? Math.round((orderStats.deliveredOrders / orderStats.totalOrders) * 100)
                                    : 0}%
                            </span>
                        </div>
                        <div className="flex justify-between items-center p-4 bg-yellow-50 rounded-lg">
                            <span className="text-gray-700">Pending Orders</span>
                            <span className="text-xl font-bold text-yellow-600">{orderStats.pendingOrders}</span>
                        </div>
                        <div className="flex justify-between items-center p-4 bg-orange-50 rounded-lg">
                            <span className="text-gray-700">Active Subscriptions</span>
                            <span className="text-xl font-bold text-orange-600">{stats.activeSubscriptions}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Insights */}
            {/* Quick Insights - Only show delivery growth for delivery role */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 border border-green-200">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <TrendingUp className="text-green-600" size={20} />
                    Quick Insights
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {isDelivery ? (
                        <>
                            <div className="bg-white/80 rounded-lg p-4">
                                <p className="text-sm text-gray-600 mb-2">Delivery Growth</p>
                                <p className="text-lg font-semibold text-green-600">+10% Efficiency</p>
                                <p className="text-xs text-gray-500 mt-1">Faster deliveries this week</p>
                            </div>
                            <div className="bg-white/80 rounded-lg p-4">
                                <p className="text-sm text-gray-600 mb-2">Customer Rating</p>
                                <p className="text-lg font-semibold text-blue-600">4.8 / 5.0</p>
                                <p className="text-xs text-gray-500 mt-1">Based on latest feedback</p>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="bg-white/80 rounded-lg p-4">
                                <p className="text-sm text-gray-600 mb-2">Top Performing Category</p>
                                <p className="text-lg font-semibold text-gray-800">Dairy Products</p>
                                <p className="text-xs text-gray-500 mt-1">Highest sales volume this period</p>
                            </div>
                            <div className="bg-white/80 rounded-lg p-4">
                                <p className="text-sm text-gray-600 mb-2">Growth Trend</p>
                                <p className="text-lg font-semibold text-green-600">+15% Revenue Growth</p>
                                <p className="text-xs text-gray-500 mt-1">Compared to last period</p>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default Reports;